import * as THREE from 'three';
import { createToonGradientMap, toonMat } from './materials.js';

// ─── Public types ────────────────────────────────────────────────────────────

export type VaultId = 'alpha' | 'beta';

export interface VaultLootItem {
  vaultId: VaultId;
  weaponKind: string; // 'sniper' | 'shotgun' | 'ar'
  rarity: string;     // 'legendary'
}

export interface VaultUpdateResult {
  openedVaultId: VaultId | null;
  collectedLoot: VaultLootItem | null;
}

export interface VaultObjectiveState {
  id: VaultId;
  x: number;
  z: number;
  unlocked: boolean;
  keycard: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const UNLOCK_RANGE   = 2.0;   // metres — E-to-unlock proximity
const LOOT_RANGE     = 1.5;   // metres — auto-collect loot proximity
const DOOR_SLIDE     = 2.4;   // world-units the door slides sideways when opening
const OPEN_DURATION  = 1.0;   // seconds for slide animation
const BOB_SPEED      = 2.0;   // rad/s for keycard bob
const BOB_AMP        = 0.12;  // metres amplitude

interface VaultDef {
  id: VaultId;
  x: number;
  z: number;
  keycard: string; // exact keycard name required
}

const VAULT_DEFS: VaultDef[] = [
  { id: 'alpha', x: -15, z: -38, keycard: 'Vault Keycard Alpha' },
  { id: 'beta',  x:  35, z:  25, keycard: 'Vault Keycard Beta'  },
];

const VAULT_LOOT_KINDS: Array<VaultLootItem['weaponKind']> = ['sniper', 'shotgun', 'ar'];

// ─── Internal state types ────────────────────────────────────────────────────

interface LootPickup {
  mesh: THREE.Mesh;
  item: VaultLootItem;
  collected: boolean;
  bobPhase: number;
}

interface VaultState {
  def: VaultDef;
  group: THREE.Group;
  door: THREE.Mesh;
  light: THREE.PointLight;
  lootPickups: LootPickup[];
  unlocked: boolean;
  openT: number; // 0 → OPEN_DURATION; -1 when not animating
  doorStartX: number;
}

// ─── FpsVaults ────────────────────────────────────────────────────────────────

export class FpsVaults {
  private readonly vaults: VaultState[] = [];
  private readonly toonGrad: THREE.DataTexture;
  private readonly interactHint: HTMLDivElement;
  private onLootCollect: ((item: VaultLootItem) => void) | null = null;
  private bobTime = 0;

  constructor(private readonly scene: THREE.Scene, mount: HTMLElement) {
    this.toonGrad = createToonGradientMap();

    // ── interaction hint ──────────────────────────────────────────────────────
    this.interactHint = document.createElement('div');
    this.interactHint.style.cssText = [
      'position:fixed',
      'bottom:22%',
      'left:50%',
      'transform:translateX(-50%)',
      'padding:6px 16px',
      'border-radius:8px',
      'background:rgba(0,0,0,0.62)',
      'border:1px solid rgba(0,200,255,0.55)',
      'color:#00e5ff',
      'font:700 13px system-ui,sans-serif',
      'letter-spacing:0.04em',
      'pointer-events:none',
      'display:none',
      'z-index:80',
    ].join(';');
    mount.appendChild(this.interactHint);

    // ── build vaults ──────────────────────────────────────────────────────────
    for (const def of VAULT_DEFS) {
      this.vaults.push(this.buildVault(def));
    }
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  setOnLootCollect(cb: (item: VaultLootItem) => void): void {
    this.onLootCollect = cb;
  }

  /**
   * Call every frame.
   * Returns any vault that opened this frame and any loot item collected.
   */
  update(dt: number, playerPos: THREE.Vector3, heldKeycards: Set<string>): VaultUpdateResult {
    this.bobTime += dt;
    const result: VaultUpdateResult = { openedVaultId: null, collectedLoot: null };

    // bob all loot pickups
    for (const vault of this.vaults) {
      for (const lp of vault.lootPickups) {
        if (!lp.collected) {
          lp.mesh.position.y = 0.6 + Math.sin(this.bobTime * BOB_SPEED + lp.bobPhase) * BOB_AMP;
          lp.mesh.rotation.y += dt * 1.2;
        }
      }
    }

    // door-open animations
    for (const vault of this.vaults) {
      if (vault.openT < 0) continue;
      vault.openT += dt;
      const u = Math.min(1, vault.openT / OPEN_DURATION);
      const ease = 1 - (1 - u) * (1 - u); // ease-out quad
      vault.door.position.x = vault.doorStartX + DOOR_SLIDE * ease;

      if (u >= 1) {
        vault.openT = -1; // animation finished
      }
    }

    // loot auto-collect
    for (const vault of this.vaults) {
      if (!vault.unlocked) continue;
      for (const lp of vault.lootPickups) {
        if (lp.collected) continue;
        const dx = playerPos.x - lp.mesh.position.x;
        const dz = playerPos.z - lp.mesh.position.z;
        if (dx * dx + dz * dz <= LOOT_RANGE * LOOT_RANGE) {
          lp.collected = true;
          lp.mesh.visible = false;
          result.collectedLoot = lp.item;
          this.onLootCollect?.(lp.item);
        }
      }
    }

    // hint visibility
    const hint = this.getVaultPromptText(playerPos, heldKeycards);
    if (hint) {
      this.interactHint.textContent = hint;
      this.interactHint.style.display = 'block';
    } else {
      this.interactHint.style.display = 'none';
    }

    return result;
  }

  /** Returns hint text when player is in range of a locked vault they can open. */
  getVaultPromptText(playerPos: THREE.Vector3, heldKeycards: Set<string>): string | null {
    for (const vault of this.vaults) {
      if (vault.unlocked) continue;
      if (!this.inUnlockRange(vault, playerPos)) continue;
      if (heldKeycards.has(vault.def.keycard)) {
        return `[E] Unlock Vault ${vault.def.id.charAt(0).toUpperCase() + vault.def.id.slice(1)}`;
      }
      return `Vault ${vault.def.id.charAt(0).toUpperCase() + vault.def.id.slice(1)} — requires ${vault.def.keycard}`;
    }
    return null;
  }

  /**
   * Call when the player presses [E].
   * Returns true if a vault was unlocked this call.
   */
  tryUnlock(playerPos: THREE.Vector3, heldKeycards: Set<string>): boolean {
    for (const vault of this.vaults) {
      if (vault.unlocked) continue;
      if (!this.inUnlockRange(vault, playerPos)) continue;
      if (!heldKeycards.has(vault.def.keycard)) continue;

      vault.unlocked = true;
      vault.openT = 0; // start animation
      vault.light.color.setHex(0x00ff44);
      vault.light.intensity = 1.2;
      return true;
    }
    return false;
  }

  getObjectiveStates(): VaultObjectiveState[] {
    return this.vaults.map((vault) => ({
      id: vault.def.id,
      x: vault.def.x,
      z: vault.def.z,
      unlocked: vault.unlocked,
      keycard: vault.def.keycard,
    }));
  }

  tryUnlockWithResult(playerPos: THREE.Vector3, heldKeycards: Set<string>): VaultId | null {
    for (const vault of this.vaults) {
      if (vault.unlocked) continue;
      if (!this.inUnlockRange(vault, playerPos)) continue;
      if (!heldKeycards.has(vault.def.keycard)) continue;

      vault.unlocked = true;
      vault.openT = 0; // start animation
      vault.light.color.setHex(0x00ff44);
      vault.light.intensity = 1.2;
      return vault.def.id;
    }
    return null;
  }

  dispose(): void {
    this.interactHint.remove();
    for (const vault of this.vaults) {
      vault.group.removeFromParent();
      vault.group.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry?.dispose();
          if (Array.isArray(o.material)) {
            o.material.forEach((m) => m.dispose());
          } else {
            (o.material as THREE.Material)?.dispose();
          }
        }
      });
      vault.light.removeFromParent();
    }
    this.toonGrad.dispose();
  }

  // ── Builder ─────────────────────────────────────────────────────────────────

  private buildVault(def: VaultDef): VaultState {
    const group = new THREE.Group();
    group.position.set(def.x, 0, def.z);
    this.scene.add(group);

    // ── floor plate ──────────────────────────────────────────────────────────
    const plate = new THREE.Mesh(
      new THREE.BoxGeometry(5, 0.06, 5),
      toonMat(0x2a2d36, this.toonGrad, { emissive: 0x0a0c10, emissiveIntensity: 0.1 }),
    );
    plate.position.y = 0.03;
    plate.receiveShadow = true;
    group.add(plate);

    // ── door frame (static) ──────────────────────────────────────────────────
    const frameMat = toonMat(0x555970, this.toonGrad, { emissive: 0x111220, emissiveIntensity: 0.12 });

    // vertical pillars
    for (const sx of [-1.15, 1.15]) {
      const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.22, 3.2, 0.28), frameMat);
      pillar.position.set(sx, 1.6, 0);
      pillar.castShadow = true;
      group.add(pillar);
    }
    // top beam
    const topBeam = new THREE.Mesh(new THREE.BoxGeometry(2.52, 0.22, 0.28), frameMat);
    topBeam.position.set(0, 3.11, 0);
    topBeam.castShadow = true;
    group.add(topBeam);

    // ── vault door (slides right on open) ────────────────────────────────────
    const doorMat = toonMat(0x2e3140, this.toonGrad, { emissive: 0x080a14, emissiveIntensity: 0.18 });
    const door = new THREE.Mesh(new THREE.BoxGeometry(2.0, 3.0, 0.4), doorMat);
    door.position.set(0, 1.5, 0);
    door.castShadow = true;
    door.receiveShadow = true;
    group.add(door);

    // golden keypad panel on door face
    const keypadMat = new THREE.MeshToonMaterial({
      color: 0xc8a830,
      gradientMap: this.toonGrad,
      emissive: new THREE.Color(0x6a4a00),
      emissiveIntensity: 0.4,
    });
    const keypad = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.42, 0.06), keypadMat);
    keypad.position.set(0.65, 1.5, 0.23);
    group.add(keypad);

    // small screen on keypad
    const screenMat = new THREE.MeshBasicMaterial({ color: 0xff2200 }); // red = locked
    const screen = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.1, 0.04), screenMat);
    screen.position.set(0.65, 1.72, 0.26);
    screen.userData.isScreen = true;
    group.add(screen);

    // ── status light (red = locked) ──────────────────────────────────────────
    const light = new THREE.PointLight(0xff2200, 0.8, 8);
    light.position.set(0, 2.5, 0.6);
    group.add(light);

    // ── interior chest ───────────────────────────────────────────────────────
    const chestMat = toonMat(0xd4a017, this.toonGrad, { emissive: 0x6a4800, emissiveIntensity: 0.55 });
    const chest = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.8, 0.8), chestMat);
    chest.position.set(0, 0.4, -1.6);
    chest.castShadow = true;
    group.add(chest);

    // chest glow
    const chestGlow = new THREE.PointLight(0xffc200, 0.9, 4);
    chestGlow.position.set(0, 0.8, -1.6);
    group.add(chestGlow);

    // ── loot pickups (3 weapons) ─────────────────────────────────────────────
    const lootPickups: LootPickup[] = [];
    const lootPositions = [
      new THREE.Vector3(-0.9, 0.6, -1.6),
      new THREE.Vector3(0.0,  0.6, -2.2),
      new THREE.Vector3(0.9,  0.6, -1.6),
    ];
    const lootColors = [0xffe066, 0xffd700, 0xffbb00];

    for (let i = 0; i < 3; i++) {
      const lootMat = new THREE.MeshToonMaterial({
        color: lootColors[i],
        gradientMap: this.toonGrad,
        emissive: new THREE.Color(0x7a5500),
        emissiveIntensity: 0.7,
      });
      const lootMesh = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.3, 0.3), lootMat);
      const lp = lootPositions[i]!;
      lootMesh.position.copy(lp);
      group.add(lootMesh);

      lootPickups.push({
        mesh: lootMesh,
        item: {
          vaultId: def.id,
          weaponKind: VAULT_LOOT_KINDS[i]!,
          rarity: 'legendary',
        },
        collected: false,
        bobPhase: (i / 3) * Math.PI * 2,
      });
    }

    // ── keycard prop (dropped separately from boss — this is decorative hint) ─
    // (Actual keycard items are managed by FpsBossFights; this is just ambience)
    const keycardMat = new THREE.MeshToonMaterial({
      color: 0x00e5ff,
      gradientMap: this.toonGrad,
      emissive: new THREE.Color(0x004466),
      emissiveIntensity: 0.9,
    });
    const keycardMesh = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.2, 0.05), keycardMat);
    keycardMesh.position.set(0, 0.8, 0.9); // mounted above keypad area
    keycardMesh.userData.isKeycardDecor = true;
    group.add(keycardMesh);

    return {
      def,
      group,
      door,
      light,
      lootPickups,
      unlocked: false,
      openT: -1,
      doorStartX: door.position.x,
    };
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  private inUnlockRange(vault: VaultState, playerPos: THREE.Vector3): boolean {
    const wx = vault.group.position.x;
    const wz = vault.group.position.z;
    const dx = playerPos.x - wx;
    const dz = playerPos.z - wz;
    return dx * dx + dz * dz <= UNLOCK_RANGE * UNLOCK_RANGE;
  }
}
